import React from "react";
import { TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import {
  PAGE_SIZE,
  fmtMoney,
  rangeLabel,
  ScoreBadge,
  LoadingBlock,
  EmptyBlock,
  ErrorBlock,
  MatchKindBadge,
  TableShell,
} from "./matching-ui";

/** Date format: "3 Feb, 2026" (no leading zero, no time) */
function fmtShortDate(d: any): string {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";

  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).formatToParts(dt);

  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const mon = parts.find((p) => p.type === "month")?.value ?? "";
  const yr = parts.find((p) => p.type === "year")?.value ?? "";
  return `${day} ${mon}, ${yr}`.trim();
}

function ymd(d: any): string | null {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const da = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function isAmountMatch(
  a: number | null | undefined,
  b: number | null | undefined,
  eps = 0.01
) {
  if (a == null || b == null) return false;
  return Math.abs(a - b) <= eps;
}

function MatchPill({ ok }: { ok: boolean }) {
  return ok ? (
    <span
      className={cn(
        "inline-flex items-center justify-center h-5 px-2 rounded-md",
        "border border-emerald-200 bg-emerald-50 text-emerald-800",
        "text-[10px] font-black tracking-widest"
      )}
    >
      Match
    </span>
  ) : (
    <span className="text-slate-300 text-[12px] font-semibold">—</span>
  );
}

function fmtMoneyWithCcy(amount: number | null | undefined, ccy?: string | null) {
  if (amount == null) return "—";
  const v = fmtMoney(amount);
  const c = (ccy ?? "").trim();
  return c ? `${c} ${v}` : v;
}

const extractRefTokens = (s?: string | null): string[] => {
  if (!s) return [];
  const up = String(s).toUpperCase().replace(/[|,;/]+/g, " ");
  const hits = up.match(/[A-Z0-9]+(?:-[A-Z0-9]+)+|[A-Z0-9]{6,32}/g) ?? [];
  return Array.from(new Set(hits.filter((tok) => /\d/.test(tok))));
};

const getRefOverlapTokens = (a?: string | null, b?: string | null): string[] => {
  const A = extractRefTokens(a);
  const B = extractRefTokens(b);
  if (!A.length || !B.length) return [];

  const setB = new Set(B);
  return A.filter((t) => setB.has(t));
};

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedReference({
  value,
  matchedTokens,
}: {
  value?: string | null;
  matchedTokens: string[];
}) {
  if (!value) return <span className="text-slate-400">—</span>;

  if (!matchedTokens.length) {
    return <span className="break-all">{value}</span>;
  }

  const uniqueTokens = Array.from(new Set(matchedTokens)).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${uniqueTokens.map(escapeRegExp).join("|")})`, "gi");
  const parts = value.split(pattern);

  return (
    <span className="break-all leading-6">
      {parts.map((part, idx) => {
        const isMatch = uniqueTokens.some((t) => t.toUpperCase() === part.toUpperCase());

        if (!isMatch) {
          return <React.Fragment key={idx}>{part}</React.Fragment>;
        }

        return (
          <span
            key={idx}
            className={cn(
              "inline-flex items-center rounded-md px-1.5 py-0.5 mx-[1px]",
              "bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200",
              "font-bold"
            )}
          >
            {part}
          </span>
        );
      })}
    </span>
  );
}

const refsOverlap = (a?: string | null, b?: string | null): boolean => {
  const A = extractRefTokens(a);
  const B = extractRefTokens(b);
  if (!A.length || !B.length) return false;
  const setB = new Set(B);
  return A.some((t) => setB.has(t));
};

function PairHeader({
  leftTitle,
  rightTitle,
  leftBadge,
  rightBadge,
  leftLabel = "Paid expense",
  rightLabel = "Matched bank transaction",
}: {
  leftTitle: React.ReactNode;
  rightTitle: React.ReactNode;
  leftBadge?: React.ReactNode;
  rightBadge?: React.ReactNode;
  leftLabel?: string;
  rightLabel?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="px-3 py-2 bg-slate-50/70">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black  tracking-widest text-slate-500">
                {leftLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {leftTitle}
              </div>
            </div>
            {leftBadge ? <div className="shrink-0">{leftBadge}</div> : null}
          </div>
        </div>

        <div className="px-3 py-2 border-t sm:border-t-0 sm:border-l border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-widest text-slate-500">
                {rightLabel}
              </div>
              <div className="mt-1 text-[12px] font-bold text-slate-900 truncate">
                {rightTitle}
              </div>
            </div>
            {rightBadge ? <div className="shrink-0">{rightBadge}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompareTable({
  txAmount,
  expAmount,
  txCcy,
  expCcy,
  txDate,
  expDate,
  txRef,
  expRef,
}: {
  txAmount: number | null | undefined;
  expAmount: number | null | undefined;
  txCcy?: string | null;
  expCcy?: string | null;
  txDate: any;
  expDate: any;
  txRef: string;
  expRef: string;
}) {
  const amountOk = isAmountMatch(txAmount, expAmount);
  const dateOk = (ymd(txDate) != null && ymd(txDate) === ymd(expDate)) || false;

  const overlapTokens = getRefOverlapTokens(expRef, txRef);
  const refOk = overlapTokens.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <table className="w-full table-fixed">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-widest w-[120px]">
              Field
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-widest">
              Bank
            </th>
            <th className="px-3 py-2 text-left text-[10px] font-black text-slate-500 tracking-widest">
              Expense
            </th>
            <th className="px-3 py-2 text-center text-[10px] font-black text-slate-500 tracking-widest w-[90px]">
              Match
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100">
          <tr className="align-top">
            <td className="px-3 py-2 text-[10px] font-black tracking-widest text-slate-500">
              Amount
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtMoneyWithCcy(txAmount, txCcy)}
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtMoneyWithCcy(expAmount, expCcy)}
            </td>
            <td className="px-3 py-2 text-center">
              <MatchPill ok={amountOk} />
            </td>
          </tr>

          <tr className="align-top">
            <td className="px-3 py-2 text-[10px] font-black tracking-widest text-slate-500">
              Date
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtShortDate(txDate)}
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900 tabular-nums">
              {fmtShortDate(expDate)}
            </td>
            <td className="px-3 py-2 text-center">
              <MatchPill ok={dateOk} />
            </td>
          </tr>

          <tr className="align-top">
            <td className="px-3 py-2 text-[10px] font-black tracking-widest text-slate-500">
              Reference ID
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
              <HighlightedReference value={txRef} matchedTokens={overlapTokens} />
            </td>
            <td className="px-3 py-2 text-[12px] font-semibold text-slate-900">
              <HighlightedReference value={expRef} matchedTokens={overlapTokens} />
            </td>
            <td className="px-3 py-2 text-center">
              <MatchPill ok={refOk} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ExtrasRow({
  expNet,
  txFee,
}: {
  expNet: number | null | undefined;
  txFee: number | null | undefined;
}) {
  if (expNet == null && txFee == null) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
        {expNet != null ? (
          <div>
            Net{" "}
            <span className="font-semibold text-slate-900 tabular-nums">
              {fmtMoney(expNet)}
            </span>
          </div>
        ) : null}

        {txFee != null ? (
          <div>
            Fee{" "}
            <span className="font-semibold text-slate-900 tabular-nums">
              {fmtMoney(txFee)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function TabMatchedPaid({
  isLoading,
  error,
  matchedPaid,
  pageMatchedPaid,
  setPageMatchedPaid,
  totalMP,
}: {
  isLoading: boolean;
  error: unknown;
  matchedPaid: any[];
  pageMatchedPaid: number;
  setPageMatchedPaid: (p: number) => void;
  totalMP: number;
}) {
  const mpItems = React.useMemo(
    () =>
      matchedPaid.slice(
        (pageMatchedPaid - 1) * PAGE_SIZE,
        pageMatchedPaid * PAGE_SIZE
      ),
    [matchedPaid, pageMatchedPaid]
  );

  const mpRange = rangeLabel(pageMatchedPaid, matchedPaid.length);

  return (
    <TabsContent value="matched-paid" className="p-4 pt-4 space-y-2">
      <div className="text-[11px] text-slate-600">
        Paid expenses that were matched with bank transactions with high confidence (99–100).
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white">
          <LoadingBlock />
        </div>
      ) : error ? (
        <ErrorBlock text="Failed to load matching data." />
      ) : matchedPaid.length === 0 ? (
        <EmptyBlock text="No matched paid expenses in this range." />
      ) : (
        <TableShell
          page={pageMatchedPaid}
          setPage={setPageMatchedPaid}
          totalPages={totalMP}
          showing={mpRange}
          total={matchedPaid.length}
          keyPrefix="mp"
        >
          <div className="space-y-2">
            {mpItems.map((row: any, idx: number) => {
              const globalRowNo = (pageMatchedPaid - 1) * PAGE_SIZE + idx + 1;

              const e = row.expense;
              const m = row.match;
              const t = m.tx;

              const paidDate = e.payment_date ?? e.expense_date;

              const expTotal = e.total_amount ?? (e as any).expense_total;
              const expCcy = e.currency ?? "";
              const expRef = e.payment_reference ?? "";

              const txAmount = t.transaction_amount;
              const txCcy = t.transaction_amount_currency ?? "";
              const txRef = t.transaction_id_reference ?? "";

              const whyText =
                Array.isArray(m.reasons) && m.reasons.length
                  ? m.reasons.slice(0, 2).join(" · ")
                  : "";

              return (
                <div
                  key={`${e.id}-${m.bank_transaction_id ?? m.tx_id ?? t.id ?? idx}`}
                  className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3 shadow-sm"
                >
                  {/* top line */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex items-baseline gap-3">
                      <div className="text-[11px] font-semibold text-slate-500 tabular-nums shrink-0">
                        #{globalRowNo}
                      </div>

                      {whyText ? (
                        <div className="min-w-0 text-[11px] text-slate-700 truncate">
                          <span className="font-black text-slate-900">WHY:</span>{" "}
                          <span className="font-semibold">{whyText}</span>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <MatchKindBadge kind={m.kind} />
                      <ScoreBadge score={m.score} />
                    </div>
                  </div>

                  {/* titles */}
                  <div className="mt-2">
                    <PairHeader
                      leftTitle={e.vendor_name ?? "Vendor"}
                      rightTitle={t.description_details ?? "Bank transaction"}
                      leftLabel="Paid expense"
                      rightLabel="Matched bank transaction"
                    />
                  </div>

                  {/* compare table */}
                  <div className="mt-2">
                    <CompareTable
                      txAmount={txAmount}
                      expAmount={expTotal}
                      txCcy={txCcy}
                      expCcy={expCcy}
                      txDate={t.transaction_date}
                      expDate={paidDate}
                      txRef={txRef}
                      expRef={expRef}
                    />
                  </div>

                  {/* extras (optional) */}
                  <div className="mt-2">
                    <ExtrasRow expNet={e.net_payment} txFee={t.transaction_fee} />
                  </div>
                </div>
              );
            })}
          </div>
        </TableShell>
      )}
    </TabsContent>
  );
}